import "./TaskPanelRight.css";

import { MandatoryFlagIcon, MilestoneDiamondIcon, RepeatIcon } from "../../../../assets";
import { WorkflowOrganisationAccessLevel } from "../../../../constants";
import { parseProjectPhase, ProjectPhase } from "../../../../constants/project-phase";
import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import {
  convertDateToStringDDMMYYYY,
  hasWorkflowOrganisationAccessLevel,
  stringKeyToSentenceCase,
} from "../../../../utils";
import {
  Autocomplete,
  Avatar,
  Button,
  Column,
  DatePicker,
  FormLabel,
  KanaTooltip,
  MultiSelect,
  RadioButtonGroup,
  ReadonlyText,
  Row,
  Select,
} from "../../../../widget";
import { DateWithIndicator } from "../../../../widget/data/DateWithIndicator";
import { useTaskPanelContext } from "../../TaskPanelContext";
import { useTaskPanelRight } from "./useTaskPanelRight";

const RADIO_BUTTON_DATA = [
  { key: true, value: "Yes" },
  { key: false, value: "No" },
];

export const TaskPanelRight = (): JSX.Element => {
  const {
    defaultSelectedOrgTypeVisibility,
    ownerOrganisationIsFixed,
    isVisibilityPinnedToCurrentOrg,
    isGeneralTask,

    stepSelectData,
    phaseOptions,
    phaseLabel = "Phase",
    organisationsSelectData,
    visibilityOrganisationsSelectData,
    activitySelectData,

    workflowOrganisationAccessLevel,
    hasFullEditAccess,

    updateStepsSelectDataByPhase,
    isRepeatablePhase,

    currentTask,
    setCurrentTask,
  } = useTaskPanelContext();

  const { currentOrganisationUuid, handleAssigneeSearch, assigneeList, handleSelectAssignee } = useTaskPanelRight({
    currentTask,
    setCurrentTask,
  });
  const phaseSelectData =
    phaseOptions ??
    Object.values(ProjectPhase).map((phase) => ({
      key: phase,
      value: stringKeyToSentenceCase(phase),
    }));
  const canRepeatForSelectedPhase =
    isRepeatablePhase !== undefined
      ? isRepeatablePhase(currentTask.phase)
      : parseProjectPhase(currentTask.phase) === ProjectPhase.ongoingMonitoring;
  return (
    <div className="TaskPanelRight">
      <div className="TaskPanelRightMainActions">
        {/* Phase & Step are workflow-only — general tasks have no workflow step. */}
        {!isGeneralTask && (
          <>
            <Row spacingV="ml">
              <Column span={12}>
                {hasFullEditAccess && currentTask.uuid === null ? (
                  <Select
                    label={`${phaseLabel}*`}
                    placeholder={`Select ${phaseLabel.toLowerCase()}`}
                    data={phaseSelectData}
                    value={currentTask.phase}
                    onChange={(value) => {
                      updateStepsSelectDataByPhase(value);
                      setCurrentTask({ ...currentTask, stepUuid: null, phase: value });
                    }}
                  />
                ) : (
                  <>
                    <FormLabel label={phaseLabel} />
                    <ReadonlyText
                      value={currentTask.phase != null ? stringKeyToSentenceCase(currentTask.phase) : currentTask.phase}
                    />
                  </>
                )}
              </Column>
            </Row>
            <Row spacingV="ml">
              <Column span={12}>
                {hasFullEditAccess ? (
                  <Select
                    label="Step*"
                    placeholder="Select step"
                    data={stepSelectData}
                    value={currentTask?.stepUuid}
                    onChange={(key) => setCurrentTask({ ...currentTask, stepUuid: key })}
                  />
                ) : (
                  <ReadonlyText
                    label="Step"
                    value={stepSelectData.find((step) => step.key === currentTask?.stepUuid)?.value}
                  />
                )}
              </Column>
            </Row>
          </>
        )}
        <Row spacingV="ml">
          <Column span={12}>
            {hasWorkflowOrganisationAccessLevel(
              WorkflowOrganisationAccessLevel.User,
              workflowOrganisationAccessLevel
            ) &&
            currentTask.assignedToUserUuid !== null &&
            currentTask.assignedToUserUuid !== "unassigned" ? (
              <Select
                label="Status*"
                placeholder="Select status"
                data={
                  currentTask.assignedToUserUuid
                    ? TaskConstants.ASSIGNED_TASK_STATUS_SELECT_DATA
                    : TaskConstants.STATUS_SELECT_DATA
                }
                value={currentTask?.status}
                onChange={(key) => setCurrentTask({ ...currentTask, status: key })}
              />
            ) : (
              <ReadonlyText
                label="Status"
                value={
                  currentTask?.status ??
                  (currentTask.assignedToUserUuid === null
                    ? TaskConstants.TASK_STATUS_UNASSIGNED
                    : TaskConstants.TASK_STATUS_TO_DO)
                }
              />
            )}
          </Column>
        </Row>

        {currentTask?.resolution && currentTask?.status === TaskConstants.TASK_STATUS_COMPLETE ? (
          <Row spacingV="ml">
            <Column span={12}>
              <ReadonlyText label="Resolution" value={currentTask?.resolution} />
            </Column>
          </Row>
        ) : null}

        {hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel) ? (
          <Row spacingV="m">
            <Column span={12}>
              <DatePicker
                label="Deadline"
                value={currentTask?.deadlineDate ?? undefined}
                onChange={(value) => setCurrentTask({ ...currentTask, deadlineDate: value ?? null })}
              />
            </Column>
          </Row>
        ) : (
          <Row spacingV="ml">
            <Column span={12}>
              <FormLabel label="Deadline" />
              {currentTask?.deadlineDate === null ? (
                <ReadonlyText
                  value={
                    currentTask?.deadlineDate ? convertDateToStringDDMMYYYY(currentTask?.deadlineDate) : "Not available"
                  }
                  noDataAvailable={currentTask?.deadlineDate === null}
                />
              ) : (
                <div className="TaskPanelDateWithIndicator">
                  <DateWithIndicator date={currentTask?.deadlineDate} format="long" />
                </div>
              )}
            </Column>
          </Row>
        )}
        {!ownerOrganisationIsFixed && organisationsSelectData.length > 0 && (
          <Row spacingV="ml">
            <Column span={12}>
              {hasFullEditAccess ? (
                <Select
                  label="Owner organisation*"
                  placeholder="Select organisation"
                  value={currentTask.ownerOrganisationUuid}
                  data={organisationsSelectData}
                  onChange={(key) => {
                    const visibility = currentTask.visibility ?? [];
                    setCurrentTask({
                      ...currentTask,
                      ownerOrganisationUuid: key,
                      visibility: visibility.includes(key) ? visibility : [...visibility, key],
                    });
                  }}
                />
              ) : (
                <ReadonlyText
                  label="Owner organisation"
                  value={organisationsSelectData.find((org) => org.key === currentTask?.ownerOrganisationUuid)?.value}
                />
              )}
            </Column>
          </Row>
        )}
        {currentTask.ownerOrganisationUuid === currentOrganisationUuid &&
        hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel) ? (
          <Row spacingV="ml">
            <Column span={currentTask.assignedToUserUuid ? "9_half" : 12}>
              <Autocomplete
                label="Assignee"
                placeholder="Search for user"
                initialKey={currentTask.assignedToUserUuid}
                initialValue={currentTask.assignedToUserName}
                data={assigneeList}
                onSelect={handleSelectAssignee}
                onTypeSearch={handleAssigneeSearch}
              />
            </Column>
            {currentTask.assignedToUserUuid && (
              <Column span="2_half">
                <Button
                  text="Clear"
                  size="small"
                  variant="secondary"
                  onClick={() =>
                    setCurrentTask({
                      ...currentTask,
                      assignedToUserUuid: null,
                      assignedToUserName: null,
                      status: TaskConstants.TASK_STATUS_UNASSIGNED,
                      // A reminder requires an assignee (BR-02794) and its control is disabled without
                      // one, so clearing the assignee has to clear the reminder with it.
                      addReminder: false,
                      reminderDate: null,
                    })
                  }
                  className="TaskPanelRemoveAssignee"
                />
              </Column>
            )}
          </Row>
        ) : (
          <Row spacingV="ml">
            <Column span={12}>
              <FormLabel
                label="Assignee"
                tooltip={
                  <KanaTooltip
                    tooltipHeader=""
                    tooltipText="The task may only be assigned by the owner organisation to one of their team"
                  />
                }
              />
              <div className="TaskPanelAssignee">
                {currentTask.assignedToUserUuid !== null && currentTask.assignedToUserName !== null && (
                  <Avatar name={currentTask.assignedToUserName} />
                )}
                <ReadonlyText
                  value={currentTask.assignedToUserName ?? "Unassigned"}
                  noDataAvailable={currentTask.assignedToUserName === null}
                />
              </div>
            </Column>
          </Row>
        )}
        {!isVisibilityPinnedToCurrentOrg && (
          <Row>
            <Column span={12}>
              {hasFullEditAccess ? (
                <MultiSelect
                  label="Visibility*"
                  placeholder="Select organisation(s)"
                  values={currentTask?.visibility}
                  data={visibilityOrganisationsSelectData}
                  onChange={(value) => {
                    let newValue = value;

                    if (defaultSelectedOrgTypeVisibility && !newValue.includes(defaultSelectedOrgTypeVisibility)) {
                      newValue = [...newValue, defaultSelectedOrgTypeVisibility];
                    }

                    if (currentTask.ownerOrganisationUuid && !newValue.includes(currentTask.ownerOrganisationUuid)) {
                      newValue = [...newValue, currentTask.ownerOrganisationUuid];
                    }

                    setCurrentTask((prev) => ({
                      ...prev,
                      visibility: newValue,
                    }));
                  }}
                />
              ) : (
                <ReadonlyText
                  label="Visibility"
                  value={currentTask?.visibility
                    ?.map((v) => visibilityOrganisationsSelectData.find((o) => o.key === v)?.value ?? v)
                    .join(", ")}
                />
              )}
            </Column>
          </Row>
        )}
      </div>
      {/* NOTE - currentTask.keyDate will always be null here KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325 */}
      {(hasFullEditAccess ||
        ((currentTask.isMilestone || activitySelectData.length > 0) &&
          hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel)) ||
        currentTask.keyDate) && (
        <div className="TaskPanelRightOtherActions">
          <Row spacingV="ml">
            <Column span={12}>
              <p className="body01">Other actions</p>
            </Column>
          </Row>
          {hasFullEditAccess && (
            <Row spacingV="xs">
              <Column span={12}>
                <div className="TaskPanelRightOtherActionsItem">
                  <FormLabel
                    label={(currentTask.isMilestone ?? false) ? "Remove as milestone task" : "Set as milestone task"}
                  />
                  <Button
                    text=""
                    icon={
                      <MilestoneDiamondIcon
                        className={`MilestoneDiamondIcon ${(currentTask.isMilestone ?? false) && "Active"}`}
                      />
                    }
                    variant="quaternary"
                    onClick={() =>
                      setCurrentTask({
                        ...currentTask,
                        isMilestone: !(currentTask.isMilestone ?? false),
                      })
                    }
                    minWidth={false}
                    hasNoPadding
                  />
                </div>
              </Column>
            </Row>
          )}
          {/* NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325 */}
          {/* {((currentTask.isMilestone &&
            hasWorkflowOrganisationAccessLevel(
              WorkflowOrganisationAccessLevel.User,
              workflowOrganisationAccessLevel
            )) ||
            currentTask.keyDate) && (
            <Row spacingV="m">
              <Column span={12}>
                {hasWorkflowOrganisationAccessLevel(
                  WorkflowOrganisationAccessLevel.User,
                  workflowOrganisationAccessLevel
                ) ? (
                  <Select
                    label="Update key project date"
                    tooltip={
                      <KanaTooltip
                        tooltipHeader=""
                        tooltipText="Selecting a date here will update the chosen key project date with this task’s date"
                      />
                    }
                    placeholder="Select key date"
                    value={currentTask?.keyDate}
                    data={phaseKeyDatesSelectData}
                    onChange={(value) => setCurrentTask({ ...currentTask, keyDate: value })}
                  />
                ) : (
                  <ReadonlyText label="Key project date" value={currentTask?.keyDate} />
                )}
              </Column>
            </Row>
          )} */}
          {hasFullEditAccess && (
            <Row spacingV={canRepeatForSelectedPhase ? "xs" : "m"}>
              <Column span={12}>
                <div className="TaskPanelRightOtherActionsItem">
                  <FormLabel
                    label={(currentTask.isMandatory ?? false) ? "Remove mandatory status" : "Set as mandatory task"}
                  />
                  <Button
                    text=""
                    icon={
                      <MandatoryFlagIcon
                        className={`MandatoryFlagIcon ${(currentTask.isMandatory ?? false) && "Active"}`}
                      />
                    }
                    variant="quaternary"
                    onClick={() =>
                      setCurrentTask({
                        ...currentTask,
                        isMandatory: !(currentTask.isMandatory ?? false),
                      })
                    }
                    minWidth={false}
                    hasNoPadding
                  />
                </div>
              </Column>
            </Row>
          )}
          {hasFullEditAccess && !isGeneralTask && canRepeatForSelectedPhase && (
            <Row spacingV="ml">
              <Column span={12}>
                <div className="TaskPanelRightOtherActionsItem">
                  <FormLabel
                    label={(currentTask.repeat ?? false) ? "Remove as repeatable task" : "Set as repeatable task"}
                  />
                  <Button
                    text=""
                    icon={<RepeatIcon className={`RepeatIcon ${(currentTask.repeat ?? false) && "Active"}`} />}
                    variant="quaternary"
                    onClick={() =>
                      setCurrentTask({
                        ...currentTask,
                        repeat: !(currentTask.repeat ?? false),
                      })
                    }
                    minWidth={false}
                    hasNoPadding
                  />
                </div>
              </Column>
            </Row>
          )}
          {/* Activity linking is out of scope for general tasks. */}
          {!isGeneralTask &&
            hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel) &&
            activitySelectData.length > 0 && (
              <Row spacingV="mxl">
                <Column span={12}>
                  <div className="TaskPanelRightOtherActionsItem">
                    <FormLabel label="Relate to activity" />
                    <RadioButtonGroup
                      data={RADIO_BUTTON_DATA}
                      value={currentTask.relateToActivity}
                      onChange={(e) =>
                        setCurrentTask({
                          ...currentTask,
                          relateToActivity: e.target.value,
                        })
                      }
                    />
                  </div>
                </Column>
              </Row>
            )}
          {!isGeneralTask &&
            hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel) &&
            currentTask.relateToActivity &&
            activitySelectData.length > 0 && (
              <Row>
                <Column span={12}>
                  <FormLabel label="Related activity" />
                  <Select
                    data={activitySelectData}
                    value={currentTask.activityUuid}
                    onChange={(value) =>
                      setCurrentTask({
                        ...currentTask,
                        activityUuid: value,
                      })
                    }
                  />
                </Column>
              </Row>
            )}
        </div>
      )}
      {(() => {
        const canEditReminder = hasWorkflowOrganisationAccessLevel(
          WorkflowOrganisationAccessLevel.User,
          workflowOrganisationAccessLevel
        );
        // BR-02794 — a reminder can only be set once the task has an assignee.
        const hasAssignee = currentTask.assignedToUserUuid !== null && currentTask.assignedToUserUuid !== "unassigned";
        // Read-only viewers with no reminder set have nothing to show.
        if (!canEditReminder && !currentTask.addReminder) return null;

        return (
          <div className="TaskPanelRightReminders">
            <Row spacingV="ml">
              <Column span={12}>
                <p className="body01">Reminders</p>
              </Column>
            </Row>
            <Row spacingV="ml">
              <Column span={12}>
                <div className="TaskPanelRightOtherActionsItem">
                  <FormLabel
                    label="Add reminder"
                    labelAlignment="left"
                    tooltipJustify="left"
                    tooltip={
                      !hasAssignee ? (
                        <KanaTooltip
                          tooltipHeader=""
                          tooltipText="A reminder can only be set once the task has an assignee"
                        />
                      ) : undefined
                    }
                  />
                  {canEditReminder ? (
                    <RadioButtonGroup
                      data={RADIO_BUTTON_DATA}
                      value={currentTask.addReminder}
                      disabled={!hasAssignee}
                      onChange={(e) => {
                        const addReminder = e.target.value as boolean;
                        setCurrentTask({
                          ...currentTask,
                          addReminder,
                          reminderDate: addReminder ? (currentTask.reminderDate ?? currentTask.deadlineDate) : null,
                        });
                      }}
                    />
                  ) : (
                    <ReadonlyText value={currentTask.addReminder ? "Yes" : "No"} />
                  )}
                </div>
              </Column>
            </Row>
            {currentTask.addReminder && (
              <Row spacingV="m">
                <Column span={12}>
                  {canEditReminder && hasAssignee ? (
                    <DatePicker
                      label="Reminder date"
                      value={currentTask.reminderDate ?? undefined}
                      onChange={(value) => setCurrentTask({ ...currentTask, reminderDate: value ?? null })}
                    />
                  ) : (
                    <>
                      <FormLabel label="Reminder date" />
                      <ReadonlyText
                        value={
                          currentTask.reminderDate
                            ? convertDateToStringDDMMYYYY(currentTask.reminderDate)
                            : "Not available"
                        }
                        noDataAvailable={currentTask.reminderDate === null}
                      />
                    </>
                  )}
                </Column>
              </Row>
            )}
          </div>
        );
      })()}
    </div>
  );
};
